<script setup>
import { computed, ref } from "vue";
import { userData } from "@/utils/vue_helpers.js";
import router from "@/router/index";
import store from "@/store/index";
import { SET_PORTAL_MODE } from "@/store/mutations/user";
import { routes } from "@/router/routes";
import {
  isProfessionalUser,
  getPortalMode,
  getDisplayRoleName,
  getDefaultRouteForMode,
  PORTAL_MODE_CLIENT,
  PORTAL_MODE_PROFESSIONAL,
} from "@/utils/permissions";

const menuOpen = ref(false);
const switchDialogOpen = ref(false);

const showSwitchProfiles = computed(() => isProfessionalUser(userData.value));

const professionalRoleLabel = computed(() =>
  getDisplayRoleName(userData.value?.profile?.role_name)
);

const currentMode = computed(() => getPortalMode());

const currentModeLabel = computed(() => {
  if (!showSwitchProfiles.value) {
    return null;
  }
  return currentMode.value === PORTAL_MODE_PROFESSIONAL
    ? professionalRoleLabel.value
    : "County Client";
});

const menuLinks = computed(() => {
  const items = [
    {
      id: "account",
      text: "Account",
      subtitle: "Dashboard & overview",
      icon: "mdi-card-account-details",
      color: "primary",
      action() {
        closeMenu();
        router.push(routes.userAccountDetails);
      },
    },
    {
      id: "profile",
      text: "Profile",
      subtitle: "Personal details",
      icon: "mdi-account-circle",
      color: "indigo",
      action() {
        closeMenu();
        router.push(routes.userProfileView);
      },
    },
  ];

  if (showSwitchProfiles.value) {
    items.push({
      id: "switch",
      text: "Switch Profiles",
      subtitle: `Currently: ${currentModeLabel.value}`,
      icon: "mdi-account-switch-outline",
      color: "teal darken-1",
      action() {
        menuOpen.value = false;
        switchDialogOpen.value = true;
      },
    });
  }

  items.push({
    id: "logout",
    text: "Logout",
    subtitle: "Sign out of the portal",
    icon: "mdi-logout",
    color: "error",
    dividerBefore: true,
    action() {
      closeMenu();
      router.push(routes.logout);
    },
  });

  return items;
});

function closeMenu() {
  menuOpen.value = false;
}

function switchToMode(portalMode) {
  store.commit(SET_PORTAL_MODE, portalMode);
  switchDialogOpen.value = false;
  router.push(getDefaultRouteForMode());
}
</script>

<template>
  <div>
    <v-menu
      v-model="menuOpen"
      offset-y
      left
      nudge-bottom="8"
      :close-on-content-click="false"
      content-class="user-dropdown-menu"
      transition="slide-y-transition"
    >
      <template #activator="{ on, attrs }">
        <v-btn
          class="user-dropdown-trigger"
          icon
          v-on="on"
          v-bind="attrs"
        >
          <v-icon color="primary" size="28">mdi-account-circle-outline</v-icon>
        </v-btn>
      </template>

      <v-sheet rounded="lg" elevation="8" class="user-dropdown-panel">
        <v-list dense class="py-2">
          <template v-for="link in menuLinks">
            <v-divider
              v-if="link.dividerBefore"
              :key="`${link.id}-divider`"
              class="my-2"
            />
            <v-list-item
              :key="link.id"
              class="user-dropdown-item mx-2 px-3"
              rounded
              @click="link.action"
            >
              <v-list-item-avatar size="36" class="mr-3 my-1">
                <v-avatar :color="`${link.color} lighten-5`" size="36">
                  <v-icon small :color="link.color">{{ link.icon }}</v-icon>
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-body-2 font-weight-medium">
                  {{ link.text }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ link.subtitle }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="my-0">
                <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>
      </v-sheet>
    </v-menu>

    <v-dialog
      v-model="switchDialogOpen"
      max-width="440"
      content-class="user-switch-dialog"
    >
      <v-card rounded="lg">
        <v-card-title class="text-h6 font-weight-bold primary--text pb-2">
          Switch profile
        </v-card-title>
        <v-card-subtitle class="pt-0 pb-4">
          Choose how you want to use the portal. You can switch anytime from this menu.
        </v-card-subtitle>

        <v-card-text class="pt-0 pb-2">
          <v-btn
            block
            large
            depressed
            rounded
            class="text-none mb-3 profile-switch-btn"
            :color="currentMode === PORTAL_MODE_CLIENT ? 'secondary' : 'grey lighten-3'"
            :class="{ 'profile-switch-btn--active': currentMode === PORTAL_MODE_CLIENT }"
            @click="switchToMode(PORTAL_MODE_CLIENT)"
          >
            <v-icon left>mdi-account</v-icon>
            County Client
            <v-chip
              v-if="currentMode === PORTAL_MODE_CLIENT"
              x-small
              label
              color="white"
              class="ml-2 font-weight-medium secondary--text"
            >
              Active
            </v-chip>
          </v-btn>

          <v-btn
            block
            large
            depressed
            rounded
            class="text-none profile-switch-btn"
            :color="currentMode === PORTAL_MODE_PROFESSIONAL ? 'primary' : 'grey lighten-3'"
            :class="{ 'profile-switch-btn--active': currentMode === PORTAL_MODE_PROFESSIONAL }"
            @click="switchToMode(PORTAL_MODE_PROFESSIONAL)"
          >
            <v-icon left>mdi-briefcase-account</v-icon>
            {{ professionalRoleLabel }}
            <v-chip
              v-if="currentMode === PORTAL_MODE_PROFESSIONAL"
              x-small
              label
              color="white"
              class="ml-2 font-weight-medium primary--text"
            >
              Active
            </v-chip>
          </v-btn>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text class="text-none" @click="switchDialogOpen = false">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped lang="scss">
.user-dropdown-trigger {
  min-width: 0 !important;
}

.user-dropdown-panel {
  min-width: 280px;
  overflow: hidden;
}

.user-dropdown-item {
  transition: background-color 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
}

.profile-switch-btn {
  justify-content: flex-start;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600;
}

.profile-switch-btn--active {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;
}
</style>

<style lang="scss">
.user-dropdown-menu {
  border-radius: 12px !important;
  overflow: visible;
}
</style>
